import { z } from "zod";
import { Id, IsoDate, IsoDateTime, Minor, Ok, OptionalText, Page, PageSize } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";

/* Mirrors core/modules/gifts: balance-based certificates (§3.7), statuses derived, never stored. */

export const GIFT_CARD_STATUSES = ["active", "redeemed", "expired", "void"] as const;
export const GiftCardStatus = z.enum(GIFT_CARD_STATUSES);
/** Stored statuses plus the two derived from the sale invoice (paid-gate). */
export const EffectiveGiftStatus = z.enum([...GIFT_CARD_STATUSES, "awaiting_payment", "sale_void"]);
export const SaleInvoiceStatus = z.enum(["issued", "paid", "void"]);

export const GiftCardListRow = z.object({
  id: Id,
  code: z.string(),
  initialMinor: Minor,
  remainingMinor: Minor,
  visitCount: z.number().int().nullable(),
  status: GiftCardStatus,
  purchaserName: z.string(),
  recipientName: z.string().nullable(),
  expiresOn: IsoDate.nullable(),
  invoiceId: Id.nullable(),
  saleInvoiceStatus: SaleInvoiceStatus.nullable(),
  patientId: Id.nullable(),
  patientName: z.string().nullable(),
  createdAt: IsoDateTime,
  effectiveStatus: EffectiveGiftStatus,
});
export type GiftCardListRow = z.infer<typeof GiftCardListRow>;

export const GiftCardListView = z.object({
  items: z.array(GiftCardListRow),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  /** Kyiv calendar day the statuses were derived against. */
  today: IsoDate,
});
export type GiftCardListView = z.infer<typeof GiftCardListView>;

export const GiftRedemptionItem = z.object({
  id: Id,
  invoiceId: Id,
  invoiceNumber: z.string(),
  amountMinor: Minor,
  at: IsoDateTime,
  reversedAt: IsoDateTime.nullable(),
});

export const GiftCardDetailView = GiftCardListRow.extend({
  message: z.string().nullable(),
  redemptions: z.array(GiftRedemptionItem),
  today: IsoDate,
});
export type GiftCardDetailView = z.infer<typeof GiftCardDetailView>;

/** The freshly sold card (its sale invoice is `invoiceId`; it stays gated until that is paid). */
export const GiftCardSnapshot = z.object({
  id: Id,
  code: z.string(),
  initialMinor: Minor,
  remainingMinor: Minor,
  visitCount: z.number().int().nullable(),
  status: GiftCardStatus,
  purchaserName: z.string(),
  recipientName: z.string().nullable(),
  message: z.string().nullable(),
  expiresOn: IsoDate.nullable(),
  invoiceId: Id.nullable(),
  patientId: Id.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type GiftCardSnapshot = z.infer<typeof GiftCardSnapshot>;

const CreateGiftCardBase = z.object({
  purchaserName: z.string().trim().min(1, "Вкажіть покупця").max(160),
  recipientName: OptionalText(160),
  message: OptionalText(500),
  expiresOn: IsoDate.optional(),
  /** Optional client binding at sale time (one certificate — one client). */
  patientId: Id.optional(),
});
/** Monetary denomination, or N visits priced server-side off the specialist's base price. */
export const CreateGiftCardBody = z.discriminatedUnion("mode", [
  CreateGiftCardBase.extend({ mode: z.literal("amount"), amountMinor: Minor.min(1) }),
  CreateGiftCardBase.extend({
    mode: z.literal("visits"),
    visitCount: z.number().int().min(1).max(100),
    specialistId: Id,
  }),
]);
export type CreateGiftCardBody = z.infer<typeof CreateGiftCardBody>;

export const RedeemGiftCardBody = z.object({
  code: z.string().trim().min(1, "Вкажіть код сертифіката").max(40),
  invoiceId: Id,
  /** Absent → min(balance, remaining due). */
  amountMinor: Minor.min(1).optional(),
});
export const RedeemedGift = z.object({
  giftCardId: Id,
  code: z.string(),
  amountMinor: Minor,
  remainingMinor: Minor,
  /** Certificates now cover the total — the invoice was auto-marked paid. */
  invoicePaid: z.boolean(),
  patientId: Id.nullable(),
});

export const AttachGiftCardBody = z.object({
  code: z.string().trim().min(1, "Вкажіть код сертифіката").max(40),
  patientId: Id,
});
export const AttachedGift = z.object({ giftCardId: Id, code: z.string(), patientId: Id });
export const DetachedGift = z.object({
  giftCardId: Id,
  code: z.string(),
  patientId: Id.nullable(),
});

export const gifts = defineRoutes({
  list: {
    method: "GET",
    path: "/gift-cards",
    summary: "Paged certificates with the effective (paid-gate / expiry) status derived",
    query: z.object({ q: z.string().max(120).optional(), page: Page, pageSize: PageSize }),
    responses: { 200: GiftCardListView, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  detail: {
    method: "GET",
    path: "/gift-cards/:giftCardId",
    summary: "One certificate with its redemption ledger",
    params: z.object({ giftCardId: Id }),
    responses: {
      200: GiftCardDetailView,
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
    },
  },
  create: {
    method: "POST",
    path: "/gift-cards",
    summary: "Sell a certificate (card + its sale invoice in one transaction)",
    body: CreateGiftCardBody,
    responses: {
      201: GiftCardSnapshot,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      422: PROBLEMS[422],
    },
  },
  redeem: {
    method: "POST",
    path: "/gift-cards/redeem",
    summary: "Apply a certificate (by code) to an issued invoice; flips it to paid when covered",
    body: RedeemGiftCardBody,
    responses: {
      200: RedeemedGift,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  attach: {
    method: "POST",
    path: "/gift-cards/attach",
    summary: "Bind a certificate (by code) to a client",
    body: AttachGiftCardBody,
    responses: {
      200: AttachedGift,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  detach: {
    method: "POST",
    path: "/gift-cards/:giftCardId/detach",
    summary: "Manager correction: unbind a certificate from its client",
    params: z.object({ giftCardId: Id }),
    responses: {
      200: DetachedGift,
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  void: {
    method: "POST",
    path: "/gift-cards/:giftCardId/void",
    summary: "Void a certificate (irreversible)",
    params: z.object({ giftCardId: Id }),
    responses: {
      200: Ok,
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
});
